import { useEffect, useRef, useState } from 'react';
import { IconPhoto } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { notifications } from '@/lib/notifications';
import { sanitizeText } from '@/utils/sanitize';
import { readLogoFile } from '@/utils/posterExport';
import { useTranslation } from '../../i18n/hooks/useTranslation';

// Small building blocks the side panels of the editor windows share (rate chart, biggapon …).

export const inputCls = 'w-full h-10 px-3 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40';

/**
 * Text input / textarea that hands the SANITIZED value (tags and control characters removed, length capped) to its owner.
 * It keeps its own draft so a space typed at the end isn't trimmed away under the user's fingers.
 */
export function TextField({ value, onChange, maxLen = 80, multiline = false, className, ...rest }) {
    const [draft, setDraft] = useState(value ?? '');
    useEffect(() => {
        if (sanitizeText(draft, { maxLen }) !== (value ?? '')) setDraft(value ?? '');
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    const handle = (e) => {
        setDraft(e.target.value);
        onChange(sanitizeText(e.target.value, { maxLen }));
    };
    const Tag = multiline ? 'textarea' : 'input';
    return (
        <Tag
            {...rest}
            value={draft}
            onChange={handle}
            maxLength={maxLen}
            className={cn(multiline ? inputCls.replace('h-10', 'min-h-[78px] py-2 leading-relaxed resize-y') : inputCls, className)}
        />
    );
}

export function Field({ label, hint, children }) {
    return (
        <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium text-zinc-700 dark:text-zinc-300">{label}</span>
            {children}
            {hint && <span className="text-[11.5px] text-zinc-400 dark:text-zinc-500">{hint}</span>}
        </label>
    );
}

/** Section heading — the same small grey caption the other editor windows use above their option groups. */
export function GroupTitle({ children }) {
    return <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">{children}</div>;
}

/** The selectable-tile look shared with the other editor windows' option grids. */
export const tileCls = (active) => (active
    ? 'border-primary bg-primary/5 dark:bg-primary/10'
    : 'border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-600');

export function OptionButton({ active, onClick, children, className, ...rest }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={cn(
                'rounded-lg border px-3 text-xs font-medium transition-colors',
                active
                    ? 'bg-primary text-white border-primary'
                    : 'border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800',
                className,
            )}
            {...rest}
        >
            {children}
        </button>
    );
}

/** A bordered row with a title, a hint and a control on the right (a switch). */
export function ToggleRow({ title, hint, children, className }) {
    return (
        <div className={cn('flex items-center gap-3 p-3.5 rounded-xl border border-zinc-200 dark:border-zinc-700', className)}>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{title}</div>
                {hint && <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 leading-snug">{hint}</div>}
            </div>
            {children}
        </div>
    );
}

/**
 * The logo block of a side panel: a preview, Upload / Change / Remove, and the checks (type allowlist, size cap) with a red
 * toast when a file is refused. `value` is the logo as a data URL ('' = none); `onChange(dataUrl | '')` gets the new one.
 */
export function LogoField({ value, onChange }) {
    const { t } = useTranslation();
    const fileRef = useRef(null);

    const handleFile = async (e) => {
        const file = e.target.files?.[0]; // snapshot first — clearing the input empties the FileList
        e.target.value = '';
        if (!file) return;
        try {
            onChange(await readLogoFile(file));
        } catch (err) {
            const key = err.message === 'type' ? 'errType' : err.message === 'size' ? 'errSize' : 'errRead';
            notifications.show({ message: t(`posterEditor.logo.${key}`), color: 'red' });
        }
    };

    return (
        <>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleFile} />
            <div className={cn('flex items-center gap-3 p-3 rounded-xl border-[1.5px] border-dashed', value ? 'border-primary/50 bg-primary/5' : 'border-zinc-300 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-800/40')}>
                <div className="w-11 h-11 shrink-0 rounded-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 flex items-center justify-center overflow-hidden text-zinc-400">
                    {value ? <img src={value} alt="" className="max-w-full max-h-full object-contain p-0.5" /> : <IconPhoto size={20} stroke={1.6} />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{t('posterEditor.logo.label')}</div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 leading-snug">{value ? t('posterEditor.logo.set') : t('posterEditor.logo.hint')}</div>
                </div>
                <button type="button" onClick={() => fileRef.current?.click()} className="px-3 py-1.5 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-[13px] font-semibold text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-700">
                    {value ? t('posterEditor.logo.change') : t('posterEditor.logo.upload')}
                </button>
                {value && (
                    <button type="button" onClick={() => onChange('')} className="px-1.5 py-1.5 text-[13px] font-semibold text-red-600 dark:text-red-400 hover:underline">{t('posterEditor.logo.remove')}</button>
                )}
            </div>
        </>
    );
}
